import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ksh, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Package, RotateCcw, Loader2, Wallet, Send, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { devLog } from "@/lib/devLog";

const STATUS_LABELS = {
  pending_payment: "Pending Payment",
  paid: "Paid",
  printing: "Printing",
  ready: "Ready for Pickup",
  completed: "Completed",
  cancelled: "Cancelled",
};
const STATUS_COLOR = {
  pending_payment: "border-[#F59E0B] text-[#F59E0B]",
  paid: "border-[#059669] text-[#059669]",
  printing: "border-[#002FA7] text-[#002FA7]",
  ready: "border-[#059669] text-[#059669] bg-[#059669]/10",
  completed: "border-[#0A0A0A] text-[#0A0A0A] bg-[#0A0A0A]/5",
  cancelled: "border-[#DC2626] text-[#DC2626]",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [reordering, setReordering] = useState(null); // order id while loading
  const [business, setBusiness] = useState(null);
  const [topupOpen, setTopupOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const loadBusiness = () => {
    api.get("/business-accounts/me")
      .then((r) => setBusiness(r.data.linked ? r.data : null))
      .catch(() => {});
  };

  useEffect(() => {
    api.get("/orders/mine")
      .then((r) => setOrders(r.data))
      .catch((err) => devLog.debug("orders/mine load failed", err));
    loadBusiness();
    // Intentional mount-only fetch — `loadBusiness` is stable (no deps) and
    // re-running on every render would spam the API.
  }, []);

  const handleReorder = async (orderId) => {
    setReordering(orderId);
    try {
      const { data } = await api.get(`/orders/mine/${orderId}/template`);
      // Stash the template — Order page picks it up on mount
      sessionStorage.setItem("ctp_reorder_template", JSON.stringify(data));
      toast.success("Specs prefilled · just drop your files");
      navigate("/order");
    } catch (e) {
      toast.error("Could not load that order — try placing a fresh one.");
    } finally {
      setReordering(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 lg:py-16">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#525252] mb-2">§ Dashboard</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
            Hello, {user?.name?.split(" ")[0] || "friend"}
          </h1>
          <p className="text-[#525252] mt-2">Your print history and active orders.</p>
        </div>
        <Link to="/order" className="bg-[#059669] text-white px-6 py-3 text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#047857] inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> New order
        </Link>
      </div>

      {business && (
        <div
          className="bg-white border-2 border-[#0A0A0A] mb-6"
          data-testid="business-wallet-badge"
          style={{ borderLeft: "6px solid #059669" }}
        >
          <div className="flex flex-wrap items-stretch">
            <div className="flex-1 min-w-[220px] p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ Office Pack</div>
              <div className="font-display font-black text-2xl mt-1">{business.account.name}</div>
              <div className="text-xs text-[#525252] mt-1 flex items-center gap-2">
                <span className="px-2 py-0.5 border border-[#059669] text-[#059669] uppercase tracking-[0.15em] text-[9px] font-bold">
                  {business.account.plan_label}
                </span>
                <span>{business.account.discount_pct}% off every order</span>
              </div>
            </div>
            <div className="flex-1 min-w-[180px] p-5 border-l border-[#E5E5E5] bg-[#FAFAFA]" data-testid="business-wallet-balance">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] flex items-center gap-1">
                <Wallet className="w-3 h-3" /> Wallet
              </div>
              <div className="font-display font-black text-3xl mono mt-1">{ksh(business.account.wallet_kes || 0)}</div>
              <div className="text-[10px] text-[#525252] mt-1">
                Orders auto-settle from this balance.
              </div>
            </div>
          </div>
          <div className="border-t border-[#E5E5E5] px-5 py-3 flex flex-wrap items-center gap-2">
            <button
              onClick={() => setTopupOpen(true)}
              data-testid="wallet-topup-open"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#0A0A0A] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#059669]"
            >
              <Send className="w-3 h-3" /> Top up via M-Pesa
            </button>
            <button
              onClick={() => setInviteOpen(true)}
              data-testid="invite-teammate-open"
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#0A0A0A] text-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#0A0A0A] hover:text-white"
            >
              <UserPlus className="w-3 h-3" /> Invite teammate
            </button>
            {business.transactions?.[0] && (
              <span className="text-[10px] text-[#525252] ml-auto">
                Last activity: <span className="mono">{new Date(business.transactions[0].created_at).toLocaleString("en-KE", { dateStyle: "short", timeStyle: "short" })}</span>
              </span>
            )}
          </div>
        </div>
      )}

      {topupOpen && business && (
        <TopUpModal
          businessName={business.account.name}
          defaultPhone={user?.phone || ""}
          onClose={() => setTopupOpen(false)}
          onSuccess={() => { setTopupOpen(false); loadBusiness(); }}
        />
      )}

      {inviteOpen && business && (
        <InviteModal
          businessName={business.account.name}
          onClose={() => setInviteOpen(false)}
        />
      )}

      {orders.length === 0 ? (
        <div className="border-2 border-dashed border-[#E5E5E5] p-12 text-center" data-testid="empty-orders">
          <Package className="w-12 h-12 mx-auto text-[#525252] mb-3" />
          <div className="font-display font-bold text-2xl">No orders yet</div>
          <p className="text-[#525252] mt-2 mb-6">When you place your first order it will appear here.</p>
          <Link to="/order" className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 text-sm uppercase tracking-[0.2em] font-bold">
            Start an order
          </Link>
        </div>
      ) : (
        <div className="border border-[#E5E5E5] bg-white overflow-x-auto" data-testid="my-orders-list">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white">
              <tr>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Tracking #</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Date</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Items</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Total</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Status</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-[#E5E5E5] last:border-b-0 hover:bg-[#F5F5F5]">
                  <td className="p-3 mono font-bold">{o.tracking_number}</td>
                  <td className="p-3 text-[#525252]">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="p-3 text-[#525252]">{o.items.length} file{o.items.length === 1 ? "" : "s"}</td>
                  <td className="p-3 mono font-bold">{ksh(o.total)}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-1 border text-[10px] uppercase tracking-[0.15em] font-bold ${STATUS_COLOR[o.status] || ""}`}>
                      {STATUS_LABELS[o.status] || o.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => handleReorder(o.id)}
                        disabled={reordering === o.id}
                        data-testid={`reorder-${o.tracking_number}`}
                        className="inline-flex items-center gap-1.5 text-[#0A0A0A] font-bold uppercase tracking-[0.15em] text-[10px] hover:text-[#059669] disabled:opacity-50"
                        title="Reuse these specs for a new order"
                      >
                        {reordering === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Reorder
                      </button>
                      <Link to={`/track?ref=${o.tracking_number}`} className="text-[#059669] font-bold uppercase tracking-[0.15em] text-[10px] hover:underline">
                        View →
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function TopUpModal({ businessName, defaultPhone, onClose, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState(defaultPhone || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!amount || parseFloat(amount) < 10) {
      toast.error("Enter at least KES 10");
      return;
    }
    if (!phone) {
      toast.error("Enter your M-Pesa number");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/business-accounts/me/topup-stk", {
        amount: parseFloat(amount),
        phone,
      });
      if (data.placeholder_mode) {
        toast.success(data.message);
        onSuccess();
      } else {
        toast.success(data.message || "STK sent — check your phone");
        // Optimistic close — the callback will credit the wallet asynchronously
        onSuccess();
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Top-up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="wallet-topup-modal">
      <div className="bg-white border-2 border-[#0A0A0A] w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ M-Pesa STK</div>
            <h3 className="font-display font-black text-2xl">Top up wallet</h3>
            <p className="text-xs text-[#525252] mt-1">Adds KES to <b>{businessName}</b>&apos;s prepaid balance.</p>
          </div>
          <button onClick={onClose} className="text-[#525252] hover:text-[#0A0A0A]" data-testid="wallet-topup-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Amount (KES)</label>
          <input
            type="number"
            min={10}
            max={300000}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 5000"
            data-testid="wallet-topup-amount"
            className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">M-Pesa phone</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="07XXXXXXXX"
            data-testid="wallet-topup-phone"
            className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
          />
        </div>
        <button
          onClick={submit}
          disabled={busy || !amount || !phone}
          data-testid="wallet-topup-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#059669] text-white text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#047857] disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send STK push
        </button>
        <p className="text-[10px] text-[#525252]">
          Enter your PIN on the phone prompt. The wallet credits automatically once Safaricom confirms.
        </p>
      </div>
    </div>
  );
}

function InviteModal({ businessName, onClose }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [acceptUrl, setAcceptUrl] = useState(null);

  const send = async () => {
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/business-accounts/me/invite", { email });
      setAcceptUrl(data.accept_url);
      toast.success(data.email_sent ? `Invite sent to ${email}` : `Invite link ready — email delivery is off, share manually.`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(acceptUrl);
      toast.success("Invite link copied");
    } catch {
      toast.error("Clipboard blocked — long-press to copy.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="invite-modal">
      <div className="bg-white border-2 border-[#0A0A0A] w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ Invite teammate</div>
            <h3 className="font-display font-black text-2xl">Add to {businessName}</h3>
            <p className="text-xs text-[#525252] mt-1">They get an emailed link; once accepted, their orders bill to this wallet.</p>
          </div>
          <button onClick={onClose} className="text-[#525252] hover:text-[#0A0A0A]" data-testid="invite-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@company.com"
            data-testid="invite-email-input"
            className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
          />
        </div>
        <button
          onClick={send}
          disabled={busy || !email}
          data-testid="invite-send-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#059669] disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
          Send invite
        </button>
        {acceptUrl && (
          <div className="border border-[#E5E5E5] p-3 bg-[#FAFAFA] space-y-2" data-testid="invite-link-preview">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252]">Share link</div>
            <div className="mono text-xs break-all">{acceptUrl}</div>
            <button
              onClick={copy}
              data-testid="invite-copy-btn"
              className="text-[10px] uppercase tracking-[0.2em] font-bold border border-[#0A0A0A] px-3 py-1.5 hover:bg-[#0A0A0A] hover:text-white"
            >
              Copy link
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
